/**
 * 角色员工授权对话框（identity-access:SC-48）。
 *
 * 对齐原型 prototype/admin/src/pages/org/RoleMembersModal.tsx：按角色维度添加/移除成员，
 * 由角色列表操作列「员工授权」打开。数据源 staffApi.listStaffs（列表随附 campusGrants，
 * 成员与候选均由其派生），写路径 staffApi.saveStaffGrants（PUT /staffs/:id/grants 整体覆盖，
 * 既有授权行 validFrom/validTo 原值透传，仅增减目标角色）。
 *
 * 受控偏差（与原型的差异）：
 * - 手机号前端掩码：后端 staff 链路 mobile 实际未脱敏（DTO 注释与实现不符），已含 * 的值原样展示；
 * - 授权校区下拉仅列启用校区（后端对停用校区保存会 400）；
 * - 员工编号列显示业务编号 staffNo（原型 dataIndex=id 为 demo 数据语义，正式工程 id 是 UUID）；
 * - 弹窗容器采用统一 DraggableModal（标题栏可拖动，其余行为同 antd Modal）。
 */
import { useEffect, useMemo, useState } from 'react';
import { App, Button, Popconfirm, Select, Space, Table } from 'antd';
import { DraggableModal } from '../../../components/DraggableModal';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import {
  staffApi,
  staffPositionLabel,
  type StaffCampusGrant,
  type StaffListItem,
} from '../../../api/staff.api';
import type { RoleDetail } from '../../../api/role.api';
import type { CampusItem } from '../../../api/campuses.api';
import { ApiError } from '../../../api/transport';

/** 员工分页单页条数（后端 MAX_PAGE_SIZE=100 硬上限）。 */
const STAFF_PAGE_SIZE = 100;
/** 全量拉取最大页数保护（防异常数据死循环）。 */
const MAX_STAFF_PAGES = 30;

/** 手机号掩码展示：已含 * 原样返回（兼容后端已脱敏的值），明文掩中段，空返回 null。 */
function maskMobileLabel(mobile?: string | null): string | null {
  if (!mobile) return null;
  if (mobile.includes('*')) return mobile;
  if (mobile.length < 7) return mobile;
  return `${mobile.slice(0, 3)}****${mobile.slice(-4)}`;
}

/** 员工是否持有指定角色（任一校区授权行含该角色即算）。 */
function hasRole(staff: StaffListItem, roleId: string): boolean {
  return (staff.campusGrants ?? []).some((g) => g.roleIds.includes(roleId));
}

/**
 * 组装「添加角色」后的授权列表（整体覆盖载荷）。
 * campusId 为空（全机构角色）：追加到所有授权行；否则目标校区已有行则追加角色，无则新建行（生效日为当天）。
 */
function buildGrantsForAdd(
  staff: StaffListItem,
  roleId: string,
  campusId?: string,
): StaffCampusGrant[] {
  const grants = staff.campusGrants ?? [];
  if (!campusId) {
    return grants.map((g) => ({ ...g, roleIds: [...new Set([...g.roleIds, roleId])] }));
  }
  if (grants.some((g) => g.campusId === campusId)) {
    return grants.map((g) =>
      g.campusId === campusId ? { ...g, roleIds: [...new Set([...g.roleIds, roleId])] } : g,
    );
  }
  return [...grants, { campusId, roleIds: [roleId], validFrom: new Date().toISOString() }];
}

/** 组装「移除角色」后的授权列表：所有行滤除该角色，授权行保留（后端允许空 roleIds 行）。 */
function buildGrantsForRemove(staff: StaffListItem, roleId: string): StaffCampusGrant[] {
  return (staff.campusGrants ?? []).map((g) => ({
    ...g,
    roleIds: g.roleIds.filter((id) => id !== roleId),
  }));
}

export interface RoleMembersModalProps {
  /** 目标角色 */
  role: RoleDetail;
  /** 校区列表（授权校区下拉与 campusId 转名称） */
  campuses: CampusItem[];
  /** 关闭回调 */
  onClose: () => void;
  /** 成员增删成功回调（供父组件刷新角色列表的成员数） */
  onMutated?: () => void;
}

export function RoleMembersModal({ role, campuses, onClose, onMutated }: RoleMembersModalProps) {
  const { message } = App.useApp();
  const [staffs, setStaffs] = useState<StaffListItem[]>([]);
  const [staffsLoading, setStaffsLoading] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | undefined>();
  const [selectedCampusId, setSelectedCampusId] = useState<string | undefined>();
  const [mutating, setMutating] = useState(false);

  // 全量拉取员工快照（含 campusGrants），成员与候选均由其派生
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStaffsLoading(true);
      try {
        const acc: StaffListItem[] = [];
        for (let page = 1; page <= MAX_STAFF_PAGES; page += 1) {
          const result = await staffApi.listStaffs({ page, pageSize: STAFF_PAGE_SIZE });
          if (cancelled) return;
          acc.push(...result.items);
          if (acc.length >= result.total || result.items.length === 0) break;
        }
        if (!cancelled) setStaffs(acc);
      } catch (err) {
        if (!cancelled && err instanceof ApiError) message.error(err.message);
      } finally {
        if (!cancelled) setStaffsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [message]);

  // 成员 = 持有该角色的员工（含停用员工：移除其角色是合法运营动作）
  const members = useMemo(
    () => staffs.filter((s) => hasRole(s, role.id)),
    [staffs, role.id],
  );
  // 候选 = 在职且未持有该角色
  const candidates = useMemo(
    () => staffs.filter((s) => s.status === 'ACTIVE' && !hasRole(s, role.id)),
    [staffs, role.id],
  );
  // 后端对停用校区保存会 400，下拉仅列启用校区
  const campusOptions = campuses
    .filter((c) => c.status === 'ACTIVE')
    .map((c) => ({ label: c.name, value: c.id }));

  const campusNameOf = (campusId: string) =>
    campuses.find((c) => c.id === campusId)?.name ?? campusId;

  /** 保存授权并同步本地快照；失败时本地不动，保持与远端一致。 */
  async function applyStaffGrants(
    staff: StaffListItem,
    nextGrants: StaffCampusGrant[],
    successText: string,
  ) {
    setMutating(true);
    try {
      await staffApi.saveStaffGrants(staff.id, { grants: nextGrants });
      setStaffs((prev) =>
        prev.map((s) => (s.id === staff.id ? { ...s, campusGrants: nextGrants } : s)),
      );
      message.success(successText);
      setSelectedStaffId(undefined);
      setSelectedCampusId(undefined);
      onMutated?.();
    } catch (err) {
      if (err instanceof ApiError) message.error(err.message);
    } finally {
      setMutating(false);
    }
  }

  /** 添加成员（校验链文案对齐原型）。 */
  function handleAdd() {
    const staff = staffs.find((s) => s.id === selectedStaffId);
    if (!staff) {
      message.warning('请选择要添加的员工');
      return;
    }
    if (role.scope === 'CAMPUS' && !selectedCampusId) {
      message.warning('校区角色需要选择授权校区');
      return;
    }
    if (role.scope === 'ORGANIZATION' && !(staff.campusGrants ?? []).length) {
      message.warning('该员工尚无任何校区授权，请先在员工管理中为其授权校区');
      return;
    }
    const campusId = role.scope === 'CAMPUS' ? selectedCampusId : undefined;
    void applyStaffGrants(
      staff,
      buildGrantsForAdd(staff, role.id, campusId),
      `已为 ${staff.name} 添加「${role.name}」角色`,
    );
  }

  /** 移除成员。 */
  function handleRemove(staff: StaffListItem) {
    void applyStaffGrants(
      staff,
      buildGrantsForRemove(staff, role.id),
      `已移除 ${staff.name} 的「${role.name}」角色`,
    );
  }

  const columns: ColumnsType<StaffListItem> = [
    {
      title: '员工编号',
      dataIndex: 'staffNo',
      render: (v: string | null | undefined, r) => v ?? r.id.slice(0, 8),
    },
    { title: '姓名', dataIndex: 'name' },
    {
      title: '岗位',
      dataIndex: 'positions',
      render: (v: StaffListItem['positions']) =>
        v.map((p) => staffPositionLabel[p] ?? p).join('、'),
    },
    {
      title: '授权校区',
      render: (_, s) =>
        (s.campusGrants ?? [])
          .filter((g) => g.roleIds.includes(role.id))
          .map((g) => campusNameOf(g.campusId))
          .join('、'),
    },
    {
      title: '操作',
      render: (_, s) => (
        <Popconfirm
          title={`确认移除 ${s.name} 的该角色？`}
          okText="确定"
          cancelText="取消"
          onConfirm={() => handleRemove(s)}
        >
          <a>移除</a>
        </Popconfirm>
      ),
    },
  ];

  return (
    <DraggableModal
      open
      title={`员工授权 · ${role.name}`}
      onCancel={onClose}
      destroyOnHidden
      width={720}
      footer={
        <Button type="primary" onClick={onClose}>
          完成
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          placeholder="选择员工"
          value={selectedStaffId}
          options={candidates.map((s) => {
            const masked = maskMobileLabel(s.mobile);
            return { label: masked ? `${s.name}（${masked}）` : s.name, value: s.id };
          })}
          onChange={setSelectedStaffId}
        />
        {role.scope === 'CAMPUS' && (
          <Select
            style={{ width: 160 }}
            placeholder="授权校区"
            value={selectedCampusId}
            options={campusOptions}
            onChange={setSelectedCampusId}
          />
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={staffsLoading || mutating}
          onClick={handleAdd}
        >
          添加
        </Button>
      </Space>
      <Table<StaffListItem>
        rowKey="id"
        size="small"
        dataSource={members}
        loading={staffsLoading}
        pagination={{ pageSize: 5 }}
        columns={columns}
      />
    </DraggableModal>
  );
}
